import { useRef, useEffect, useState, FormEvent } from 'react';
import { trackPhoneClick, trackLineClick, trackEmailClick, trackFormSubmit } from '@/hooks/useAnalytics';

const genreOptions = [
  '貴金属・金', '宝石・ダイヤ', 'ブランド品', 'カメラ', 'お酒', '金券',
  '骨董品', '家電', '楽器', '電動工具', 'ゲーム', 'スマホ・タブレット', 'その他',
];

export default function Contact() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const message = data.get('message') as string;
    if (message && message.length > 500) {
      alert('お問い合わせ内容は500文字以内でご入力ください。');
      return;
    }

    const encoded = new URLSearchParams();
    data.forEach((value, key) => {
      encoded.append(key, value as string);
    });

    setStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d76u1o7lpdqvlupfq62g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encoded.toString(),
      });
      if (res.ok) {
        trackFormSubmit('contact_form');
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-stone-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-amber-100/40 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-amber-100/30 translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-12">
          <span className={`text-amber-600 text-sm font-semibold tracking-widest uppercase mb-3 block transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            CONTACT
          </span>
          <h2
            className={`text-4xl md:text-5xl font-black text-stone-900 mb-4 transition-all duration-700 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: '\'Noto Serif JP\', serif' }}
          >
            福岡・九州の出張買取<br />お問い合わせ・買取予約
          </h2>
          <p className={`text-stone-500 text-base max-w-md mx-auto leading-relaxed transition-all duration-700 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            九州全域への出張・査定費は無料です。お気軽にご連絡ください。<br />
            24時間以内にご連絡いたします。
          </p>
        </div>

        {/* Quick Contact */}
        <div className={`flex flex-col sm:flex-row gap-4 mb-10 transition-all duration-700 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <a
            href="tel:0120-676-337"
            onClick={() => trackPhoneClick('contact')}
            className="flex-1 flex items-center justify-center gap-3 py-4 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-phone-fill text-xl" />
            </div>
            <div className="text-left">
              <div className="text-xs font-normal opacity-80">電話でお問い合わせ</div>
              <div className="text-lg font-black">0120-676-337</div>
            </div>
          </a>
          <a
            href="https://lin.ee/OGQq9z6"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackLineClick('contact')}
            className="flex-1 flex items-center justify-center gap-3 py-4 rounded-xl text-white font-bold transition-colors cursor-pointer"
            style={{ backgroundColor: '#06C755' }}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-line-line text-xl" />
            </div>
            <div className="text-left">
              <div className="text-xs font-normal opacity-80">LINEでお問い合わせ</div>
              <div className="text-base font-bold">公式LINEはこちら</div>
            </div>
          </a>
          <a
            href="mailto:info@botan-buy.com"
            onClick={() => trackEmailClick('contact')}
            className="flex-1 flex items-center justify-center gap-3 py-4 rounded-xl bg-stone-800 text-white font-bold hover:bg-stone-700 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-mail-fill text-xl" />
            </div>
            <div className="text-left">
              <div className="text-xs font-normal opacity-70">メールでお問い合わせ</div>
              <div className="text-base font-bold">info@botan-buy.com</div>
            </div>
          </a>
        </div>

        {/* Form */}
        <div className={`bg-white border border-stone-200 rounded-2xl p-8 md:p-10 transition-all duration-700 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h3 className="text-stone-900 font-bold text-xl mb-6">福岡・九州 無料出張査定のお申し込み</h3>

          {status === 'success' ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 text-3xl mx-auto mb-4">
                <i className="ri-check-line" />
              </div>
              <h4 className="text-stone-900 font-bold text-xl mb-2">送信が完了しました</h4>
              <p className="text-stone-500 text-sm">24時間以内にご連絡いたします。少々お待ちください。</p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-6 px-6 py-3 rounded-full bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                別のお問い合わせをする
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              {/* Name + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-stone-600 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="例：天神 太郎"
                    className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 text-stone-900 text-sm placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="例：090-0000-0000"
                    className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 text-stone-900 text-sm placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-stone-600 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                  メールアドレス
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="例：sample@email.com"
                  className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 text-stone-900 text-sm placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {/* Address + Genre */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-stone-600 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    お住まいの地域 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    required
                    placeholder="例：福岡県福岡市"
                    className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 text-stone-900 text-sm placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    買取希望品目
                  </label>
                  <select
                    name="genre"
                    className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 text-stone-900 text-sm focus:outline-none focus:border-amber-400 transition-colors cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    {genreOptions.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preferred Date */}
              <div>
                <label className="block text-stone-600 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                  ご希望の日時
                </label>
                <input
                  type="text"
                  name="preferred_date"
                  placeholder="例：2024年4月15日（月）午後、または平日の午前中など"
                  className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 text-stone-900 text-sm placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {/* Message */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-stone-600 text-xs font-semibold uppercase tracking-wide">
                    お問い合わせ内容
                  </label>
                  <span className={`text-xs ${charCount > 450 ? 'text-red-500' : 'text-stone-400'}`}>
                    {charCount}/500
                  </span>
                </div>
                <textarea
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="買取品の詳細、数量、その他ご要望などをご記入ください"
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 rounded-lg bg-stone-50 border border-stone-200 text-stone-900 text-sm placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                />
              </div>

              {/* Error */}
              {status === 'error' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                  <i className="ri-error-warning-line" />
                  送信に失敗しました。時間をおいて再度お試しください。
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-4 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-bold text-base transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    送信中...
                  </>
                ) : (
                  <>
                    無料査定を申し込む
                    <i className="ri-arrow-right-line" />
                  </>
                )}
              </button>

              <p className="text-stone-400 text-xs text-center">
                ご入力いただいた個人情報は、査定・お問い合わせ対応にのみ使用いたします。
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
